import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SelectInputField from "../common-components/SelectInputField";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Modal from "@mui/material/Modal";
import { CrossIcon, ModalTop, ModalBottom } from "../../assets/Icon/ModalIcon";
import {
  CloseButtonWrapper,
  CloseButton,
  ModalWrapper,
} from "../style/modalStyle";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { TrashIcon } from "../../assets/Icon/headerCicon";
import "./AddCustomerInfo.css";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  retrieveCustomerList,
  createCustomer,
  createInvoice,
  getInvoiceDetails,
  updateInvoiceItem,
} from "../../stores/clients";
import ListLoadingNew from "../common-components/ListLoadingNew";
import { setToast } from "../../stores/toast/toastslice";
import CircularProgress from "@mui/material/CircularProgress";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
dayjs.extend(utc);

const validationSchema = Yup.object({
  customerType: Yup.string().required("Customer is required"),
  invoiceDate: Yup.string().required("Invoice date is required"),
  dueDate: Yup.string().required("Due date is required"),
  notes: Yup.string(),
  tax: Yup.string(),
});

// Define a type for line items
type LineItem = {
  item: string;
  description: string;
  unit: string;
  quantity: string;
  amount: string;
};

const AddCustomerInfo = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    customerList,
  } = useSelector((state: any) => state.clients);
  const location = useLocation();

  const [openModal, setOpenModal] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [lineItems, setLineItems] = React.useState<LineItem[]>([]);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [submittingType, setSubmittingType] = useState<null | "draft" | "save">(
    null
  );
  const [invoiceDetails, setInvoiceDetails] = React.useState<any>(null);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [invoiceDateOpen, setInvoiceDateOpen] = React.useState(false);
  const [dueDateOpen, setDueDateOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isViewOnly) {
      dispatch<any>(retrieveCustomerList({ offset: 0, limit: 10, search: "" }));
    }
    if (location.state?.invoice_id) {
      setLoadingDetails(true);
      dispatch<any>(getInvoiceDetails(location.state.invoice_id)).then(
        (result: any) => {
          // result.payload contains the API response
          if (result?.payload?.data?.invoice) {
            setInvoiceDetails(result.payload?.data?.invoice);
            // Prefill line items if present
            if (result.payload?.data?.invoice_line_items) {
              setLineItems(
                result.payload?.data?.invoice_line_items.map((item: any) => ({
                  item: item.item_name || "",
                  description: item.description || "",
                  unit: item.unit_cost ? String(item.unit_cost) : "",
                  quantity: item.quantity ? String(item.quantity) : "",
                  amount:
                    item.unit_cost && item.quantity
                      ? (
                          Number(item.unit_cost) * Number(item.quantity)
                        ).toFixed(2)
                      : "",
                }))
              );
            }
          }
          setLoadingDetails(false);
        }
      );
    }
    console.log("addcus.tsx");
  }, [dispatch, location.state]);

  const isViewOnly = Boolean(
    location.state?.invoice_id && location.state?.viewOnly
  );

  // Handler to add a new line item
  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        item: "",
        description: "",
        unit: "",
        quantity: "1",
        amount: "",
      },
    ]);
  };

  // Handler to update a field in a line item
  const handleLineItemChange = (
    index: number,
    field: keyof LineItem,
    value: string
  ) => {
    // Prevent more than 6 digits for unit and quantity
    if (
      (field === "unit" || field === "quantity") &&
      value.replace(/\D/g, "").length > 6
    ) {
      return;
    }

    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;

    // Recalculate amount if unit or quantity changes
    if (field === "unit" || field === "quantity") {
      const rawUnit = updatedItems[index].unit || "0";
      const rawQty = updatedItems[index].quantity || "1";

      const unit = parseFloat(rawUnit) || 0;
      const quantity = parseFloat(rawQty) || 1; // default to 1 if empty or 0

      const amount = unit * quantity;
      updatedItems[index].amount = amount ? amount.toFixed(2) : "";
    }

    setLineItems(updatedItems);
  };

  // Handler to delete a line item
  const handleDeleteLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Function to handle creating a customer from the modal form
  const handleCreateCustomer = async (
    values: { name: string; email: string },
    { setSubmitting, resetForm, setErrors }: any
  ) => {
    try {
      const response = await dispatch<any>(
        createCustomer({ email: values.email, name: values.name }) as any
      ).unwrap();
      setOpenModal(false);
      resetForm();
      if (response.code === 200) {
        dispatch<any>(
          setToast({
            message: "New customer created successfully!",
            type: "success",
            title: "Success",
            onClose: () => {},
          })
        );
        dispatch<any>(
          retrieveCustomerList({ offset: 0, limit: 10, search: "" })
        );
      }
      if (response.code === 400 || response?.code === 422) {
        dispatch<any>(
          setToast({
            message: response.message,
            type: "error",
            title: "Error",
            onClose: () => {},
          })
        );
      }
    } catch (err: any) {
      setErrors({ email: err?.message || "Failed to create customer" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvoiceSubmit = async (
    values: any,
    { setSubmitting, setErrors }: any
  ) => {
    // Find selected customer
    const selectedCustomer = (customerList || []).find(
      (c: any) => String(c.id) === String(values.customerType)
    );
    if (!selectedCustomer) {
      setErrors({ customerType: "Please select a valid customer." } as any);
      setSubmitting(false);
      return;
    }
    // Build payload
    const createPayload: any = {
      customer_id: String(selectedCustomer.id),
      customer_name:
        selectedCustomer.name ||
        selectedCustomer.full_name ||
        selectedCustomer.customer_name ||
        "",
      customer_email: selectedCustomer.email || "",
      invoice_date: values.invoiceDate
        ? values.invoiceDate.startOf("day").toISOString()
        : null,
      save_to_draft: saveAsDraft,
      invoice_due_date: values.dueDate
        ? values.dueDate.startOf("day").toISOString()
        : null,
      tax_rate: Number(values.tax),
      line_items: lineItems.map((item) => ({
        item_name: item.item,
        description: item.description,
        unit_cost: Number(item.unit),
        quantity: Number(item.quantity),
      })),
      notes: values.notes,
    };
    const createPayloadUpdate: any = {
      customer_id: String(selectedCustomer.id),
      invoice_id:  location.state?.invoice_id,
      customer_name: 
        selectedCustomer.name ||
        selectedCustomer.full_name ||
        selectedCustomer.customer_name ||
        "",
      customer_email: selectedCustomer.email || "",
      invoice_date: values.invoiceDate
        ? values.invoiceDate.startOf("day").toISOString()
        : null,
      save_to_draft: saveAsDraft,
      invoice_due_date: values.dueDate
        ? values.dueDate.startOf("day").toISOString()
        : null,
      tax_rate: Number(values.tax),
      line_items: lineItems.map((item) => ({
        item_name: item.item,
        description: item.description,
        unit_cost: Number(item.unit),
        quantity: Number(item.quantity),
      })),
      notes: values.notes,
    };
    try {
      if (location.state?.invoice_id && isViewOnly === false) {
        // Update invoice: send full payload
        const response = await dispatch<any>(
          updateInvoiceItem(createPayloadUpdate) as any
        ).unwrap();
        console.log(response);
        if (response?.code === 200) {
          dispatch<any>(
            setToast({
              message: "Invoice updated successfully!",
              type: "success",
              title: "Success",
              onClose: () => {},
            })
          );
          navigate("/tools/invoices");
        }
        if (response?.code === 400 || response?.code === 422) {
          dispatch<any>(
            setToast({
              message: response?.message || "Some error occured!",
              type: "error",
              title: "Error",
              onClose: () => {},
            })
          );
        }
      } else {
        // Create invoice
        const response = await dispatch<any>(
          createInvoice(createPayload) as any
        ).unwrap();
        if (response?.code === 400 || response?.code === 422) {
          dispatch<any>(
            setToast({
              message: response?.message || "Some error occured!",
              type: "error",
              title: "Error",
              onClose: () => {},
            })
          );
          return;
        }
        dispatch<any>(
          setToast({
            message: "Invoice created successfully!",
            type: "success",
            title: "Success",
            onClose: () => {},
          })
        );
        navigate("/tools/invoices");
      }
    } catch (err: any) {
      dispatch<any>(
        setToast({
          message: err?.message || "Failed to save invoice",
          type: "error",
          title: "Error",
          onClose: () => {},
        })
      );
    } finally {
      setSubmitting(false);
      setSubmittingType(null);
    }
  };

  return (
    <Box
      sx={{
        marginX: isMobile ? "10px" : "18px",
        // overflow: "hidden"
      }}
    >
      {loadingDetails && location.state?.invoice_id ? (
        <ListLoadingNew />
      ) : (
        <>
          <Button
            sx={{
              mb: 2,
              color: "#6C6F71",
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              fontWeight: 600,
              gap: 0.5,
              ml: -2,
            }}
            onClick={() => navigate("/tools/invoices")}
          >
            <ArrowBackIosNewIcon style={{}} /> Back
          </Button>
          <div
            style={{
              color: "#060110",
              fontWeight: 500,
              fontSize: "24px",
            }}
          >
            Customer Info
          </div>
          <Formik
            enableReinitialize
            initialValues={{
              customerType: invoiceDetails?.customer_id || "",
              invoiceDate: invoiceDetails?.invoice_date
                ? dayjs(invoiceDetails.invoice_date)
                : null,
              dueDate: invoiceDetails?.invoice_due_date
                ? dayjs(invoiceDetails.invoice_due_date)
                : null,
              notes: invoiceDetails?.notes || "",
              tax: invoiceDetails?.tax_rate
                ? String(invoiceDetails.tax_rate)
                : "",
            }}
            validationSchema={validationSchema}
            validate={(values) => {
              const errors: any = {};
              if (lineItems.length === 0) {
                errors.lineItems = "At least 1 line item is required.";
              } else {
                const hasEmpty = lineItems.some(
                  (item) =>
                    !item.item || !item.unit || !item.quantity || !item.amount
                );
                if (hasEmpty) {
                  errors.lineItems = "Please fill all the required fields";
                }
              }
              // Due date must be equal or after invoice date
              if (values.invoiceDate && values.dueDate) {
                if (values.dueDate.isBefore(values.invoiceDate, "day")) {
                  errors.dueDate = "Due date cannot be before invoice date";
                }
              }
              return errors;
            }}
            onSubmit={handleInvoiceSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldValue,
              submitCount,
            }) => {
              // Custom watcher component for due date logic
              function DueDateWatcher() {
                useEffect(() => {
                  if (
                    values?.invoiceDate &&
                    values?.dueDate &&
                    values.dueDate.isBefore(values.invoiceDate, "day")
                  ) {
                    setFieldValue("dueDate", null);
                  }
                }, [values?.invoiceDate, values?.dueDate, setFieldValue]);
                return null;
              }

              // Calculate subtotal from line items
              const subtotalFromLineItems = lineItems.reduce((sum, item) => {
                const amount = parseFloat(item.amount) || 0;
                return sum + amount;
              }, 0);

              // Use invoiceDetails data in view-only mode, otherwise use calculated line items
              const subtotal =
                isViewOnly && invoiceDetails?.total_amount
                  ? invoiceDetails.total_amount
                  : subtotalFromLineItems;

              const taxPercent = parseFloat(values.tax || "0") || 0;
              const taxAmount = (subtotal * taxPercent) / 100;

              // Calculate total: subtotal + tax
              const total = subtotal + taxAmount;

              return (
                <Form>
                  <DueDateWatcher />
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      maxWidth: "484px",
                    }}
                  >
                    <div>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mt: 2,
                        }}
                      >
                        <label
                          style={{
                            fontWeight: 500,
                            fontSize: "16px",
                            color: "#2C3034",
                            marginBottom: 0,
                            display: "block",
                          }}
                        >
                          Customer
                        </label>
                        {!isViewOnly && (
                          <Button
                            sx={{
                              fontSize: "16px",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              height: 4,
                            }}
                            onClick={() => setOpenModal(true)}
                          >
                            <AddCircleOutlineOutlinedIcon
                              style={{
                                width: "16px",
                              }}
                            />
                            Add New Customer
                          </Button>
                        )}
                      </Box>
                      <SelectInputField
                        placeholder="Select Customer"
                        label=""
                        menuData={(() => {
                          let list = customerList
                            ? customerList.map((c: any) => ({
                                ...c,
                                name:
                                  c.name ||
                                  c.full_name ||
                                  c.customer_name ||
                                  c.email,
                              }))
                            : [];
                          // In viewOnly mode, ensure the current customer is present
                          if (isViewOnly && invoiceDetails?.customer_id) {
                            const exists = list.some(
                              (c: any) =>
                                String(c.id) ===
                                String(invoiceDetails.customer_id)
                            );
                            if (!exists) {
                              list = [
                                {
                                  id: invoiceDetails.customer_id,
                                  name:
                                    invoiceDetails.customer_name ||
                                    invoiceDetails.customer_full_name ||
                                    invoiceDetails.customer_email ||
                                    invoiceDetails.customer_id,
                                },
                                ...list,
                              ];
                            }
                          }
                          return list;
                        })()}
                        name="customerType"
                        formik={{
                          ...{
                            values,
                            errors,
                            touched,
                            setFieldValue: (field: any, value: any) =>
                              handleChange({
                                target: { name: field, value: String(value) },
                              }),
                          },
                        }}
                        sendValue={false}
                        disabled={isViewOnly}
                      />
                    </div>
                  </Box>
                  {/* Modal for Add New Customer */}
                  <Modal open={openModal} onClose={() => setOpenModal(false)}>
                    <ModalWrapper
                      sx={{
                        width: isMobile ? "350px" : "550px",
                        p: 0,
                        height: "405px",
                        backgroundImage: "none",
                        position: "relative",
                        borderRadius: "20px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Top right decoration */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          zIndex: 1,
                        }}
                      >
                        <ModalTop />
                      </div>
                      {/* Bottom left decoration */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: -6,
                          left: 0,
                          zIndex: 1,
                        }}
                      >
                        <ModalBottom />
                      </div>
                      {/* Ensure close button is above decorations */}
                      <CloseButtonWrapper
                        sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
                      >
                        <CloseButton onClick={() => setOpenModal(false)}>
                          <CrossIcon />
                        </CloseButton>
                      </CloseButtonWrapper>
                      <Box sx={{ p: 4, pt: 0 }}>
                        <div
                          style={{
                            fontSize: "24px",
                            color: "#01060B",
                            fontWeight: 700,
                            marginTop: 5,
                            marginBottom: 20,
                          }}
                        >
                          Add New Customer
                        </div>
                        <Formik
                          initialValues={{ name: "", email: "" }}
                          validationSchema={Yup.object({
                            name: Yup.string().required("Name is required"),
                            email: Yup.string()
                              .matches(
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                "Invalid email"
                              )
                              .email("Invalid email")
                              .required("Email is required"),
                          })}
                          onSubmit={handleCreateCustomer}
                          validateOnChange={true}
                        >
                          {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            isSubmitting,
                            handleSubmit,
                          }) => (
                            <form onSubmit={handleSubmit}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: errors.email || errors.name ? 1 : 2,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 7,
                                  }}
                                >
                                  <label
                                    style={{
                                      fontWeight: 500,
                                      color: "#2C3034",
                                      fontSize: "16px",
                                    }}
                                  >
                                    Email Address
                                  </label>
                                  <input
                                    style={{
                                      padding: "16px",
                                      borderRadius: "8px",
                                      border: "0.5px solid #A7C7EA",
                                      fontSize: "16px",
                                      outline: "none",
                                      transition: "border 0.2s",
                                      boxShadow: "0 1px 8px rgba(0, 0, 0, 0.1)",
                                    }}
                                    placeholder="Enter email address"
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={(e) =>
                                      (e.target.style.border =
                                        "2px solid #2977CC")
                                    }
                                  />
                                  {touched.email && errors.email && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "14px",
                                        marginTop: "-5px",
                                      }}
                                    >
                                      {errors.email}
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 7,
                                  }}
                                >
                                  <label
                                    style={{
                                      fontWeight: 500,
                                      color: "#2C3034",
                                      fontSize: "16px",
                                    }}
                                  >
                                    Customer Name
                                  </label>
                                  <input
                                    style={{
                                      padding: "16px",
                                      borderRadius: "8px",
                                      border: "0.5px solid #A7C7EA",
                                      fontSize: "16px",
                                      outline: "none",
                                      transition: "border 0.2s",
                                      boxShadow: "0 1px 8px rgba(0, 0, 0, 0.1)",
                                    }}
                                    name="name"
                                    placeholder="Enter Customer Name"
                                    maxLength={60}
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={(e) =>
                                      (e.target.style.border =
                                        "2px solid #2977CC")
                                    }
                                  />
                                  {touched.name && errors.name && (
                                    <span
                                      style={{
                                        color: "red",
                                        fontSize: "14px",
                                        marginTop: "-5px",
                                      }}
                                    >
                                      {errors.name}
                                    </span>
                                  )}
                                </div>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 2,
                                    mt: 1,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Button
                                    sx={{
                                      border: "2px solid #2977CC",
                                      borderRadius: "8px",
                                      width: "192px",
                                      height: "51px",
                                      fontSize: "16px",
                                      fontWeight: 600,
                                      color: "#2977CC",
                                    }}
                                    onClick={() => setOpenModal(false)}
                                  >
                                    Close
                                  </Button>
                                  <Button
                                    sx={{
                                      background:
                                        "linear-gradient(to right, #2977CC, #1D538F)",
                                      color: "white",
                                      width: "192px",
                                      height: "51px",
                                      fontSize: "16px",
                                      fontWeight: 600,
                                    }}
                                    type="submit"
                                    disabled={isSubmitting}
                                  >
                                    {isSubmitting ? (
                                      <CircularProgress
                                        size={20}
                                        sx={{ color: "#fff" }}
                                      />
                                    ) : (
                                      "Save"
                                    )}
                                  </Button>
                                </Box>
                              </Box>
                            </form>
                          )}
                        </Formik>
                      </Box>
                    </ModalWrapper>
                  </Modal>
                  <Box>
                    <div>
                      <p
                        style={{
                          fontWeight: 500,
                          fontSize: "24px",
                          color: "#060110",
                          marginTop: 30,
                          marginBottom: 25,
                        }}
                      >
                        Invoice Details
                      </p>
                      {/**DATE PICKERS */}
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en-gb"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection:
                              isMobile || isTablet ? "column" : "row",
                            gap: 2,
                            width: "100%",
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Invoice Date */}
                          <div>
                            <label
                              style={{
                                fontWeight: 500,
                                color: "#2C3034",
                                fontSize: "16px",
                                marginBottom: 10,
                                display: "block",
                              }}
                            >
                              Invoice Date
                            </label>
                            <DatePicker
                              format="DD/MM/YYYY"
                              value={values.invoiceDate}
                              minDate={dayjs().startOf("day")}
                              onChange={(val) =>
                                setFieldValue(
                                  "invoiceDate",
                                  val
                                    ? dayjs.utc(val.format("YYYY-MM-DD"))
                                    : null
                                )
                              }
                              open={invoiceDateOpen}
                              onOpen={() => setInvoiceDateOpen(true)}
                              onClose={() => setInvoiceDateOpen(false)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "medium",
                                  sx: {
                                    flex: 1,
                                    minWidth:
                                      isMobile || isTablet ? "100%" : "482px",
                                    boxShadow: "0px 2px 4px 0px #D1DEEB33",
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        border: "none",
                                      },
                                      "&:hover fieldset": {
                                        border: "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        border: "0.5px solid #2977CC",
                                      },
                                    },
                                  },
                                  inputProps: { readOnly: true },
                                  onClick: () => setInvoiceDateOpen(true),
                                },
                              }}
                              disabled={isViewOnly}
                            />
                            {touched.invoiceDate && errors.invoiceDate && (
                              <div
                                style={{
                                  color: "red",
                                  fontSize: "16px",
                                  marginTop: "4px",
                                  marginLeft: "4px",
                                }}
                              >
                                {errors.invoiceDate}
                              </div>
                            )}
                          </div>

                          {/* Due Date */}
                          <div>
                            <label
                              style={{
                                fontWeight: 500,
                                color: "#2C3034",
                                fontSize: "16px",
                                marginBottom: 10,
                                display: "block",
                              }}
                            >
                              Due Date
                            </label>
                            <DatePicker
                              format="DD/MM/YYYY"
                              value={values.dueDate}
                              minDate={
                                values.invoiceDate
                                  ? values.invoiceDate
                                  : dayjs().startOf("day")
                              }
                              onChange={(val) =>
                                setFieldValue(
                                  "dueDate",
                                  val
                                    ? dayjs.utc(val.format("YYYY-MM-DD"))
                                    : null
                                )
                              }
                              open={dueDateOpen}
                              onOpen={() => setDueDateOpen(true)}
                              onClose={() => setDueDateOpen(false)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "medium",
                                  sx: {
                                    flex: 1,
                                    minWidth:
                                      isMobile || isTablet ? "100%" : "482px",
                                    boxShadow: "0px 2px 4px 0px #D1DEEB33",
                                    "& .MuiOutlinedInput-root": {
                                      "& fieldset": {
                                        border: "none",
                                      },
                                      "&:hover fieldset": {
                                        border: "none",
                                      },
                                      "&.Mui-focused fieldset": {
                                        border: "0.5px solid #2977CC",
                                      },
                                    },
                                  },
                                  inputProps: { readOnly: true },
                                  onClick: () => setDueDateOpen(true),
                                },
                              }}
                              disabled={isViewOnly}
                            />
                            {touched.dueDate && errors.dueDate && (
                              <div
                                style={{
                                  color: "red",
                                  fontSize: "16px",
                                  marginTop: "4px",
                                  marginLeft: "4px",
                                }}
                              >
                                {errors.dueDate}
                              </div>
                            )}
                          </div>
                        </Box>
                      </LocalizationProvider>
                      {/* <div
                        style={{
                          width: isMobile ? "300px" : "482px",
                          marginTop: 25,
                        }}
                      >
                        <label
                          style={{
                            fontWeight: 500,
                            color: "#2C3034",
                            fontSize: "16px",
                            display: "block",
                          }}
                        >
                          Currency
                        </label>
                        <SelectInputField
                          placeholder="Select Currency"
                          label=""
                          menuData={currencyOptions}
                          name="currency"
                          formik={{
                            ...{
                              values,
                              errors,
                              touched,
                              setFieldValue: (field: any, value: any) =>
                                setFieldValue(field, value),
                            },
                          }}
                          sendValue={true}
                          disabled={isViewOnly}
                        />
                      </div> */}
                    </div>
                  </Box>

                  {/**LIST ITEM */}
                  <Box
                    sx={{
                      background: "white",
                      // padding: 3,
                      borderRadius: "10px",
                      boxShadow: "0px 2px 4px 0px #D1DEEB33",
                      my: 5,
                      paddingBottom: 3,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingRight: "20px",
                        paddingLeft: "20px",
                        paddingTop: "20px",
                        paddingBottom: "0px",
                      }}
                    >
                      <p
                        style={{
                          color: "#060110",
                          fontSize: isMobile ? "20px" : "24px",
                          fontWeight: 500,
                        }}
                      >
                        Line Items
                      </p>
                      {!isViewOnly && (
                        <Button
                          sx={{
                            fontSize: "12px",
                            px: 1.5,
                            py: 1,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "white",
                            background: "#2977CC",
                            width: "131px",
                            height: isMobile ? "38px" : "41px",
                          }}
                          onClick={handleAddLineItem}
                        >
                          <AddCircleOutlineOutlinedIcon
                            style={{
                              width: "16px",
                            }}
                          />
                          Add new item
                        </Button>
                      )}
                    </div>

                    {/* Line Items Table */}
                    <Box
                      sx={{ mt: 3, mb: 3, width: "100%", overflowX: "auto" }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          minWidth: "600px",
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#F4F7FBB2" }}>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "left",
                                fontWeight: 500,
                                color: "#6C6F71",
                                paddingLeft: "25px",
                              }}
                            >
                              ITEM NAME
                            </th>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "left",
                                fontWeight: 500,
                                color: "#6C6F71",
                              }}
                            >
                              DESCRIPTION{" "}
                              <span
                                style={{
                                  fontSize: "12px",
                                }}
                              >
                                (OPTIONAL)
                              </span>
                            </th>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "left",
                                fontWeight: 500,
                                color: "#6C6F71",
                              }}
                            >
                              UNIT COST
                            </th>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "left",
                                fontWeight: 500,
                                color: "#6C6F71",
                              }}
                            >
                              QUANTITY
                            </th>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "left",
                                fontWeight: 500,
                                color: "#6C6F71",
                              }}
                            >
                              AMOUNT
                            </th>
                            <th
                              style={{
                                fontSize: "14px",
                                padding: "10px",
                                textAlign: "center",
                                fontWeight: 600,
                                color: "#2C3034",
                              }}
                            ></th>
                          </tr>
                        </thead>
                        {lineItems.length > 0 && (
                          <tbody>
                            {lineItems.map((item, idx) => (
                              <tr
                                key={idx}
                                style={{ borderBottom: "2px solid #EAF1FA99" }}
                              >
                                <td style={{ padding: "20px" }}>
                                  <input
                                    className="line-item-input"
                                    type="text"
                                    maxLength={60}
                                    value={item.item}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        idx,
                                        "item",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Item Name"
                                    style={{
                                      width: "118px",
                                      height: "36px",
                                      fontSize: "16px",
                                      borderRadius: 6,
                                      padding: 8,
                                      background: "#fff",
                                    }}
                                    disabled={isViewOnly}
                                  />
                                  {item.item.length === 60 && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: 14,
                                        marginTop: 2,
                                      }}
                                    >
                                      Cannot exceed 60 characters
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: "8px" }}>
                                  <input
                                    className="line-item-input"
                                    maxLength={200}
                                    type="text"
                                    value={item.description}
                                    onChange={(e) =>
                                      handleLineItemChange(
                                        idx,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Description"
                                    style={{
                                      width: "224px",
                                      height: "36px",
                                      fontSize: "16px",
                                      borderRadius: 6,
                                      padding: 8,
                                      background: "#fff",
                                    }}
                                    disabled={isViewOnly}
                                  />
                                  {item.description.length === 200 && (
                                    <div
                                      style={{
                                        color: "red",
                                        fontSize: 14,
                                        marginTop: 2,
                                      }}
                                    >
                                      Cannot exceed 200 characters
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: "8px" }}>
                                  <input
                                    className="line-item-input"
                                    type="text"
                                    maxLength={6}
                                    value={item.unit}
                                    onChange={(e) => {
                                      // Allow only numbers and a single decimal point, but not as the first character
                                      let val = e.target.value.replace(
                                        /[^0-9.]/g,
                                        ""
                                      );
                                      // Prevent decimal as first character
                                      if (val.startsWith(".")) {
                                        val = "";
                                      }
                                      // Only one decimal allowed
                                      val = val.replace(/(\..*)\./g, "$1");
                                      // Limit to 2 decimal places
                                      if (val.includes(".")) {
                                        const parts = val.split(".");
                                        if (parts[1] && parts[1].length > 2) {
                                          val =
                                            parts[0] +
                                            "." +
                                            parts[1].substring(0, 2);
                                        }
                                      }
                                      handleLineItemChange(idx, "unit", val);
                                    }}
                                    placeholder="Cost"
                                    style={{
                                      width: "77px",
                                      height: "36px",
                                      fontSize: "16px",
                                      borderRadius: 6,
                                      padding: 8,
                                      background: "#fff",
                                    }}
                                    disabled={isViewOnly}
                                  />
                                  {item.unit &&
                                    item.unit.replace(/\D/g, "").length > 6 && (
                                      <div
                                        style={{
                                          color: "red",
                                          fontSize: 12,
                                          marginTop: 2,
                                        }}
                                      >
                                        Unit cost cannot exceed 6 digits.
                                      </div>
                                    )}
                                </td>
                                <td style={{ padding: "8px" }}>
                                  <input
                                    className="line-item-input"
                                    type="text"
                                    value={item.quantity}
                                    onChange={(e) => {
                                      // Remove non-digit characters
                                      let val = e.target.value.replace(
                                        /[^0-9]/g,
                                        ""
                                      );

                                      // Prevent leading zeros
                                      if (val.length > 1) {
                                        val = val.replace(/^0+/, "");
                                      }

                                      // Prevent entering '0' as the only value
                                      if (val === "0") val = "1";

                                      handleLineItemChange(
                                        idx,
                                        "quantity",
                                        val
                                      );
                                    }}
                                    placeholder="Qty."
                                    style={{
                                      width: "52px",
                                      height: "36px",
                                      fontSize: "16px",
                                      borderRadius: 6,
                                      padding: 8,
                                      background: "#fff",
                                    }}
                                    disabled={isViewOnly}
                                  />
                                  {item.quantity &&
                                    item.quantity.replace(/\D/g, "").length >
                                      6 && (
                                      <div
                                        style={{
                                          color: "red",
                                          fontSize: 12,
                                          marginTop: 2,
                                        }}
                                      >
                                        Quantity cannot exceed 6 digits.
                                      </div>
                                    )}
                                </td>
                                <td style={{ padding: "8px" }}>
                                  <div
                                    className="line-item-input"
                                    style={{
                                      minWidth: "120px",
                                      width: "max-content",
                                      maxWidth: "100%",
                                      height: "36px",
                                      fontSize: "16px",
                                      borderRadius: 6,
                                      padding: 4,
                                      background: "#fff",
                                      color:
                                        item?.amount &&
                                        parseFloat(item.amount) > 0
                                          ? "#01060B"
                                          : "#97999B",
                                      textAlign: "left",
                                      overflowX: "auto",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {"$"}
                                    {item?.amount
                                      ? parseFloat(item.amount).toFixed(2)
                                      : "0.00"}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: "8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {!isViewOnly && (
                                    <Button
                                      onClick={() => handleDeleteLineItem(idx)}
                                      sx={{
                                        width: "20px",
                                      }}
                                    >
                                      <TrashIcon />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        )}
                      </table>
                      {lineItems.length === 0 && (
                        <div
                          style={{
                            background: "#ffffff",
                            color: "#6C6F71",
                            fontSize: "14px",
                            padding: 10,
                            marginTop: 3,
                            marginBottom: 5,
                            textAlign: "center",
                            borderBottom: "2px solid #EAF1FA99",
                          }}
                        >
                          No Line item added!
                        </div>
                      )}
                    </Box>

                    {(errors as any).lineItems && submitCount > 0 && (
                      <div
                        style={{
                          color: "red",
                          fontSize: "16px",
                          marginTop: lineItems?.length === 0 ? "0px" : "-20px",
                          marginLeft: "20px",
                        }}
                      >
                        {(errors as any).lineItems}
                      </div>
                    )}

                    {/**SUBTOTAL */}
                    <div
                      style={{
                        margin: 20,
                        background: "#EAF1FA99",
                        padding: isMobile ? 20 : 30,
                        borderRadius: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <p
                          style={{
                            color: "#2C3034",
                            fontWeight: 500,
                            fontSize: isMobile ? "14px" : "18px",
                          }}
                        >
                          SUBTOTAL
                        </p>
                        <div
                          style={{
                            background: "#83B0E166",
                            height: 1,
                            width: "100%",
                          }}
                        />
                        <p
                          style={{
                            color: "#2977CC",
                            fontSize: isMobile ? "14px" : "18px",
                            fontWeight: 600,
                          }}
                        >
                          ${subtotal.toFixed(2)}
                        </p>
                      </div>
                      {/* <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          marginTop: 30,
                        }}
                      >
                        <p
                          style={{
                            color: "#2C3034",
                            fontWeight: 500,
                            fontSize: isMobile ? "14px" : "18px",
                            width: isMobile ? "100px" : "90px",
                          }}
                        >
                          TAX (%)
                        </p>
                        <div
                          style={{
                            background: "#83B0E166",
                            height: 1,
                            width: "100%",
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 5,
                          }}
                        >
                          <input
                            className="line-item-input"
                            name="tax"
                            type="text"
                            maxLength={4}
                            value={values.tax}
                            onChange={(e) => {
                              let val = e.target.value.replace(/[^0-9.]/g, "");
                              if (val.startsWith(".")) {
                                val = "";
                              }
                              val = val.replace(/(\..*)\./g, "$1");
                              if (val.includes(".")) {
                                const parts = val.split(".");
                                if (parts[1] && parts[1].length > 2) {
                                  val =
                                    parts[0] + "." + parts[1].substring(0, 2);
                                }
                              }
                              handleChange({
                                target: { name: "tax", value: val },
                              });
                            }}
                            onBlur={handleBlur}
                            style={{
                              border: "0.5px solid #A7C7EA",
                              borderRadius: "4px",
                              width: isMobile ? "90px" : "120px",
                              height: isMobile ? "40px" : "48px",
                              background: "transparent",
                              fontSize: isMobile ? "12px" : "16px",
                              paddingLeft: 10,
                            }}
                            placeholder="Add tax (%)"
                            disabled={isViewOnly}
                          />
                        </div>
                      </div> */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 10,
                          marginTop: 30,
                        }}
                      >
                        <p
                          style={{
                            color: "#2977CC",
                            fontWeight: 500,
                            fontSize: isMobile ? "14px" : "18px",
                          }}
                        >
                          TOTAL
                        </p>
                        <div
                          style={{
                            background: "#83B0E166",
                            height: 1,
                            width: "100%",
                          }}
                        />
                        <p
                          style={{
                            color: "#2977CC",
                            fontSize: isMobile ? "14px" : "18px",
                            fontWeight: 600,
                          }}
                        >
                          ${total.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </Box>
                  <Box>
                    <p
                      style={{
                        color: "#2C3034",
                        fontWeight: 500,
                        fontSize: "16px",
                        marginBottom: 10,
                      }}
                    >
                      Notes
                    </p>
                    <textarea
                      name="notes"
                      value={values.notes}
                      maxLength={500}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter notes here..."
                      onFocus={() => setIsFocused(true)}
                      style={{
                        border: `${
                          isFocused ? "1px solid #2977CC" : "2px solid #EAF1FA"
                        }`,
                        background: "white",
                        fontSize: "16px",
                        borderRadius: "10px",
                        width: isMobile ? "100%" : "482px",
                        height: "141px",
                        padding: 20,
                        resize: "none",
                        fontFamily: "Plus Jakarta Sans",
                        outline: "none", // removes blue default outline
                      }}
                      disabled={isViewOnly}
                    />
                  </Box>
                  {!isViewOnly && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        my: 5,
                      }}
                    >
                      <div
                        onClick={() => {
                          setSaveAsDraft(true);
                          setSubmittingType("draft");
                        }}
                        style={{
                          width: isMobile ? "100%" : "",
                        }}
                      >
                        <Button
                          sx={{
                            width: isMobile ? "100%" : "191px",
                            height: "52px",
                            fontSize: "16px",
                            fontWeight: 600,
                            background:
                              isSubmitting && submittingType === "draft"
                                ? "#BDBDBD"
                                : "#EAF1FA",
                            borderRadius: "8px",
                            color:
                              isSubmitting && submittingType === "draft"
                                ? "#fff"
                                : undefined,
                            position: "relative",
                            pointerEvents:
                              isSubmitting && submittingType === "draft"
                                ? "none"
                                : "auto",
                            opacity:
                              isSubmitting && submittingType === "draft"
                                ? 0.7
                                : 1,
                          }}
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting && submittingType === "draft" ? (
                            <CircularProgress
                              size={20}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            "Save as draft"
                          )}
                        </Button>
                      </div>
                      <Button
                        sx={{
                          width: isMobile ? "100%" : "191px",
                          height: "52px",
                          fontSize: "16px",
                          fontWeight: 600,
                          borderRadius: "8px",
                          background:
                            isSubmitting && submittingType === "save"
                              ? "#BDBDBD"
                              : "linear-gradient(98.24deg, #2977CC 4.13%, #1D538F 128.24%)",
                          color: "white !important",
                          position: "relative",
                          pointerEvents:
                            isSubmitting && submittingType === "save"
                              ? "none"
                              : "auto",
                          opacity:
                            isSubmitting && submittingType === "save" ? 0.7 : 1,
                        }}
                        onClick={() => {
                          setSaveAsDraft(false);
                          setSubmittingType("save");
                        }}
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting && submittingType === "save" ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </Box>
                  )}
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </Box>
  );
};

export default AddCustomerInfo;
